import { isBlazor } from '@syncfusion/ej2-base';
import { Node, Lane } from '../objects/node';
import { Diagram } from '../diagram';
import { Selector } from '../objects/node';
import { Size } from '../primitives/size';
import { cloneObject } from './../utility/base-util';
import { getObjectType } from './../utility/diagram-util';
import { Rect } from '../primitives/rect';
import { getAdornerLayerSvg } from '../utility/dom-util';
import { swimLaneMeasureAndArrange, checkLaneSize, checkPhaseOffset, canLaneInterchange } from '../utility/swim-lane-util';
import { updatePhaseMaxWidth, updateHeaderMaxWidth, updateConnectorsProperties } from '../utility/swim-lane-util';
import { considerSwimLanePadding } from '../utility/swim-lane-util';
import { DiagramAction, DiagramConstraints, NodeConstraints } from '../enum/enum';
import { getDiagramElement } from '../utility/dom-util';
/**
 * Interaction for Container
 */
//#region canvas Container interaction
/**
 * updateCanvasBounds method\
 *
 * @returns {  void }    updateCanvasBounds method .\
 * @param {Diagram} diagram - provide the diagram value.
 * @param {NodeModel | ConnectorModel} obj - provide the isVertical value.
 * @param {PointModel} position - provide the position value.
 * @param {boolean} isBoundsUpdate - provide the isBoundsUpdate value.
 * @private
 */
export function updateCanvasBounds(diagram, obj, position, isBoundsUpdate) {
    let container;
    const connectorList = [];
    let groupAction = false;
    if (checkParentAsContainer(diagram, obj, true)) {
        diagram.protectPropertyChange(true);
        container = diagram.nameTable[obj.parentId];
        const wrapper = container.wrapper;
        if (diagram.selectedItems.nodes.length > 1) {
            diagram.multiselect = true;
        }
        else {
            diagram.multiselect = false;
        }
        if (container && container.container.type === 'Canvas') {
            if ((isBoundsUpdate || (wrapper.bounds.x <= position.x && wrapper.bounds.right >= position.x &&
                (wrapper.bounds.y <= position.y && wrapper.bounds.bottom >= position.y) && !diagram.multiselect))) {
                let parentWrapper;
                const y = wrapper.bounds.y;
                const x = wrapper.bounds.x;
                const parent = diagram.nameTable[container.parentId] || container;
                const shape = parent.shape;
                if (shape.type === 'SwimLane') {
                    groupAction = updateLaneBoundsAfterAddChild(container, parent, obj, diagram, true);
                }
                else {
                    const parent = diagram.nameTable[container.parentId] || container;
                    const shape = parent.shape;
                    parentWrapper = parent.wrapper;
                    if (wrapper.actualSize.width < wrapper.outerBounds.width &&
                        (!(wrapper.bounds.x > wrapper.outerBounds.x))) {
                        if (container.rowIndex !== undefined) {
                            //const columnIndex:number = parent.columns.length - 1;
                            parentWrapper.updateColumnWidth(container.columnIndex, wrapper.outerBounds.width, true);
                            if (shape.orientation === 'Horizontal' && shape.phaseSize) {
                                updatePhaseMaxWidth(parent, diagram, wrapper, container.columnIndex);
                            }
                            updateHeaderMaxWidth(diagram, parent);
                            diagram.drag(parent, x - wrapper.bounds.x, y - wrapper.bounds.y);
                        }
                        else {
                            diagram.scale(container, (1 + ((wrapper.outerBounds.width - wrapper.actualSize.width) / wrapper.actualSize.width)), 1, ((wrapper.outerBounds.x < wrapper.bounds.x) ? { x: 1, y: 0.5 } : { x: 0, y: 0.5 }));
                        }
                    }
                    if (wrapper.actualSize.height < wrapper.outerBounds.height &&
                        (!(wrapper.bounds.y > wrapper.outerBounds.y))) {
                        if (container.rowIndex !== undefined) {
                            parentWrapper.updateRowHeight(container.rowIndex, wrapper.outerBounds.height, true);
                            diagram.drag(parent, x - wrapper.bounds.x, y - wrapper.bounds.y);
                        }
                        else {
                            diagram.scale(container, 1, (1 + ((wrapper.outerBounds.height - wrapper.actualSize.height) / wrapper.actualSize.height)), ((wrapper.outerBounds.y < wrapper.bounds.y) ? { x: 0.5, y: 1 } : { x: 0.5, y: 0 }));
                        }
                    }
                }
            }
            diagram.select([obj]);
            updateConnectorsProperties(connectorList, diagram);
        }
        diagram.protectPropertyChange(false);
    }
    return groupAction;
}
/**
 * removeChildInContainer method\
 *
 * @returns {  void }    removeChildInContainer method .\
 * @param {Diagram} diagram - provide the diagram value.
 * @param {NodeModel | ConnectorModel} obj - provide the isVertical value.
 * @param {PointModel} position - provide the position value.
 * @param {boolean} isBoundsUpdate - provide the isBoundsUpdate value.
 * @private
 */
export function removeChildInContainer(diagram, obj, position, isBoundsUpdate) {
    let container; //let connectorList: string[] = [];
    if (checkParentAsContainer(diagram, obj, true) && !(diagram.cancelPositionChange)) {
        const isProtectedOnChange = 'isProtectedOnChange';
        const propertyChangeValue = diagram[`${isProtectedOnChange}`];
        diagram.protectPropertyChange(true);
        container = diagram.nameTable[obj.parentId];
        const wrapper = container.wrapper;
        if (container && container.container.type === 'Canvas') {
            if ((!isBoundsUpdate && (!(wrapper.bounds.x <= position.x && wrapper.bounds.right >= position.x &&
                (wrapper.bounds.y <= position.y && wrapper.bounds.bottom >= position.y))))) {
                if (!(obj.constraints & NodeConstraints.AllowMovingOutsideLane)) {
                    const undoObj = cloneObject(obj);
                    diagram.clearSelection();
                    removeChildrenInLane(diagram, obj);
                    obj.parentId = '';
                    //EJ2-65676 - Exception throws on updating node annotation after drag and drop from swimlane to diagram canvas.
                    obj.parentObj = diagram;
                    obj.propName = 'nodes';
                    const entry = {
                        type: 'ChildCollectionChanged', category: 'Internal',
                        undoObject: undoObj, redoObject: cloneObject(obj)
                    };
                    diagram.addHistoryEntry(entry);
                    if (diagram.commandHandler.isContainer) {
                        diagram.commandHandler.isContainer = false;
                        diagram.endGroupAction();
                    }
                    moveSwinLaneChild(obj, diagram);
                }
            }
        }
        diagram.protectPropertyChange(propertyChangeValue);
    }
}
/**
 * findBounds method\
 *
 * @returns {  NodeModel | ConnectorModel  }    findBounds method .\
 * @param {NodeModel} obj - provide the diagram value.
 * @param {number} columnIndex - provide the isVertical value.
 * @param {boolean} isHeader - provide the isVertical value.
 * @private
 */
export function findBounds(obj, columnIndex, isHeader) {
    const rect = new Rect();
    const rows = (obj.shape.type === 'SwimLane') ?
        obj.wrapper.children[0].rows : obj.wrapper.rows;
    for (let i = ((isHeader) ? 1 : 0); i < rows.length; i++) {
        rect.uniteRect(rows[parseInt(i.toString(), 10)].cells[parseInt(columnIndex.toString(), 10)].bounds);
    }
    return rect;
}
/**
 * createHelper method\
 *
 * @returns {  NodeModel | ConnectorModel  }    createHelper method .\
 * @param {Diagram} diagram - provide the diagram value.
 * @param {NodeModel | ConnectorModel} obj - provide the isVertical value.
 * @private
 */
export function createHelper(diagram, obj) {
    let newObj;
    const cloneObject = {};
    for (const prop of Object.keys(obj)) {
        cloneObject[`${prop}`] = obj[`${prop}`];
    }
    if (getObjectType(obj) === Node) {
        newObj = new Node(diagram, 'nodes', cloneObject, true);
        newObj.id = obj.id;
        diagram.initObject(newObj);
    }
    diagram.updateDiagramObject(newObj);
    return newObj;
}
/**
 * renderContainerHelper method\
 *
 * @returns {  NodeModel | ConnectorModel  }    renderContainerHelper method .\
 * @param {Diagram} diagram - provide the diagram value.
 * @param {NodeModel | ConnectorModel} obj - provide the isVertical value.
 * @private
 */
export function renderContainerHelper(diagram, obj) {
    diagram.enableServerDataBinding(false);
    let object;
    let container;
    let nodes;
    if (!isBlazor()) {
        if (diagram.selectedObject.helperObject) {
            nodes = diagram.selectedObject.helperObject;
        }
        else if (diagram.selectedItems.nodes.length > 0 || diagram.selectedItems.connectors.length > 0) {
            if (obj instanceof Selector && obj.nodes.length + obj.connectors.length === 1) {
                object = (obj.nodes.length > 0) ? obj.nodes[0] : obj.connectors[0];
                container = diagram.selectedItems.wrapper.children[0];
            }
            else {
                object = obj;
                //Removed isBlazor code
                container = diagram.selectedItems.wrapper;
            }
            diagram.selectedObject.actualObject = object;
            // Added below code to render helper object for multiselected nodes inside swimlane to avoid interaction related issues.
            const dragObject = object instanceof Selector
                ? (object.nodes.length > 0 ? object.nodes[0] : object) : object;
            diagram.selectedObject.actualObject = object;
            if ((!diagram.currentSymbol) && (((dragObject.isLane && canLaneInterchange(dragObject, diagram) &&
                checkParentAsContainer(diagram, dragObject))
                || ((!dragObject.isLane) && checkParentAsContainer(diagram, dragObject))) ||
                ((diagram.constraints & DiagramConstraints.LineRouting) && diagram.selectedItems.nodes.length > 0))) {
                const node = {
                    id: 'helper',
                    rotateAngle: container.rotateAngle,
                    offsetX: container.offsetX, offsetY: container.offsetY,
                    minWidth: container.minWidth, minHeight: container.minHeight,
                    maxWidth: container.maxWidth, maxHeight: container.maxHeight,
                    width: container.actualSize.width,
                    height: container.actualSize.height,
                    pivot: container.pivot,
                    style: { strokeDashArray: '2 2', fill: 'transparent', strokeColor: '#7D7D7D', strokeWidth: 2 }
                };
                nodes = createHelper(diagram, node);
                diagram.selectedObject.helperObject = nodes;
            }
        }
    }
    diagram.enableServerDataBinding(true);
    return nodes;
}
/**
 * checkParentAsContainer method\
 *
 * @returns {  void  }    checkParentAsContainer method .\
 * @param {Diagram} diagram - provide the diagram value.
 * @param {NodeModel | ConnectorModel} obj - provide the isVertical value.
 * @param {boolean} isChild - provide the isChild value.
 * @private
 */
export function checkParentAsContainer(diagram, obj, isChild) {
    const parentNode = (isChild) ? diagram.nameTable[obj.parentId] :
        (diagram.nameTable[obj.parentId] || obj);
    if (parentNode && parentNode.container) {
        return true;
    }
    return false;
}
/**
 * checkChildNodeInContainer method\
 *
 * @returns {  void  }    checkChildNodeInContainer method .\
 * @param {Diagram} diagram - provide the diagram value.
 * @param {NodeModel} obj - provide the isVertical value.
 * @private
 */
export function checkChildNodeInContainer(diagram, obj) {
    const parentNode = diagram.nameTable[obj.parentId];
    obj.laneMargin = { left: obj.margin.left, right: obj.margin.right, top: obj.margin.top, bottom: obj.margin.bottom };
    //To handle the resize of bpmn node which has text annotation inside swimlane
    if (obj.hasTextAnnotation) {
        for (let i = 0; i < obj.outEdges.length; i++) {
            const connector = diagram.nameTable[obj.outEdges[parseInt(i.toString(), 10)]];
            if (connector.isBpmnAnnotationConnector) {
                const textNode = diagram.nameTable[connector.targetID];
                const oldObject = { width: obj.wrapper.actualSize.width, height: obj.wrapper.actualSize.height,
                    offsetX: obj.wrapper.offsetX, offsetY: obj.wrapper.offsetY };
                const oldX = obj.wrapper.offsetX;
                const oldY = obj.wrapper.offsetY;
                const offset = diagram.getTextAnnotationOffset(obj, textNode, oldObject, oldX, oldY);
                const difX = offset.x - oldX;
                const difY = offset.y - oldY;
                if (difX || difY || obj.width !== obj.wrapper.actualSize.width || obj.height !== obj.wrapper.actualSize.height) {
                    obj.isResized = true;
                    if (!obj.resizeDif) {
                        obj.resizeDif = [];
                    }
                    obj.resizeDif[textNode.id] = { x: difX, y: difY };
                }
            }
        }
    }
    if (parentNode && parentNode.container.type === 'Canvas') {
        obj.margin.left = (obj.offsetX - parentNode.wrapper.bounds.x - (obj.width / 2));
        obj.margin.top = (obj.offsetY - parentNode.wrapper.bounds.y - (obj.height / 2));
    }
    diagram.nodePropertyChange(obj, {}, {
        width: obj.width, height: obj.height,
        offsetX: obj.offsetX, offsetY: obj.offsetY,
        margin: {
            left: obj.margin.left,
            right: obj.margin.right, top: obj.margin.top,
            bottom: obj.margin.bottom
        }, rotateAngle: obj.rotateAngle
    });
    //EJ2-913789 - Lane size gets varied upon undo redo the node drop
    if (diagram.undoRedoModule && parentNode && parentNode.isLane && diagram.undoRedoModule.checkRedo &&
        (obj.margin.left < 0 || obj.margin.top < 0)) {
        removeChildrenInLane(diagram, obj);
    }
    if (parentNode && !parentNode.isLane) {
        parentNode.wrapper.measure(new Size());
        parentNode.wrapper.arrange(parentNode.wrapper.desiredSize);
    }
}
function removeChildrenInLane(diagram, node) {
    if (node.parentId && node.parentId !== '') {
        const prevParentNode = diagram.nameTable[node.parentId];
        if (prevParentNode.isLane && prevParentNode.parentId) {
            const swimlane = diagram.nameTable[prevParentNode.parentId];
            const canvasId = (prevParentNode.id.slice(swimlane.id.length));
            const prevParentId = canvasId.substring(0, canvasId.length - 1);
            const lanes = swimlane.shape.lanes;
            let lane;
            for (let i = 0; i < lanes.length; i++) {
                lane = lanes[parseInt(i.toString(), 10)];
                if (prevParentId === lane.id) {
                    for (let j = 0; j < lane.children.length; j++) {
                        if (lane.children[parseInt(j.toString(), 10)].id === node.id) {
                            lane.children.splice(j, 1);
                            j--;
                        }
                    }
                }
            }
        }
        diagram.deleteChild(node);
    }
}
/**
 *
 * @param { NodeModel | ConnectorModel} obj - Provide the obj value.
 * @param { NodeModel } swimlane  - Provide the swimlane value.
 * @param { Diagram } diagram - Provide the diagram value.
 * @returns { void } updateZindex method .\
 * @private
 */
export function updateZindex(obj, swimlane, diagram) {
    const objLayer = diagram.commandHandler.getObjectLayer(obj.id);
    const zIndexTable = objLayer.zIndexTable;
    let targetZIndex = swimlane.zIndex + 1;
    // Find a unique zIndex
    while (zIndexTable[parseInt(targetZIndex.toString(), 10)] !== undefined) {
        targetZIndex++;
    }
    // Assign the unique zIndex to the node
    delete objLayer.zIndexTable[obj.zIndex];
    obj.zIndex = targetZIndex;
    objLayer.zIndexTable[parseInt(targetZIndex.toString(), 10)] = obj.id;
}
/**
 * addChildToContainer method\
 *
 * @returns {  void  }    addChildToContainer method .\
 * @param {DiagramElement} diagram - provide the element value.
 * @param {boolean} parent - provide the isVertical value.
 * @param {PointModel} node - provide the node value.
 * @param {Diagram} isUndo - provide the isUndo value.
 * @param {boolean} historyAction - provide the historyAction value.
 * @param {boolean} canUpdateZindex - provide the canUpdateZindex value.
 * @private
 */
export function addChildToContainer(diagram, parent, node, isUndo, historyAction, canUpdateZindex) {
    if (!diagram.currentSymbol) {
        diagram.protectPropertyChange(true);
        let swimlane = diagram.nameTable[parent.parentId];
        node = diagram.getObject(node.id) || node;
        //Bug 909155: Issue in connecting nodes with ports.
        //When we add child nodes at runtime and perform undo redo, the child node is not selectable as the zIndex of swimlane is higher than child node.
        //Added below code to update the zIndex of the child node.
        if (swimlane && swimlane.zIndex > node.zIndex && canUpdateZindex) {
            updateZindex(node, swimlane, diagram);
        }
        const child = (diagram.nodes.indexOf(node) !== -1) ? node.id : node;
        if (parent.container.type === 'Canvas' && !historyAction) {
            const left = (node.wrapper.offsetX - node.wrapper.actualSize.width * node.pivot.x) -
                (parent.wrapper.offsetX - parent.wrapper.actualSize.width * parent.wrapper.pivot.x);
            const top = (node.wrapper.offsetY - node.wrapper.actualSize.height * node.pivot.y) -
                (parent.wrapper.offsetY - parent.wrapper.actualSize.height * parent.wrapper.pivot.y);
            node.margin.left = left;
            node.margin.top = top;
        }
        else if (swimlane) {
            const swimLaneBounds = swimlane.wrapper.bounds;
            const parentBounds = parent.wrapper.bounds;
            if (swimlane.shape.orientation === 'Horizontal') {
                node.margin.left -= parentBounds.x - swimLaneBounds.x;
            }
            else {
                const laneHeaderId = parent.parentId + swimlane.shape.lanes[0].id + '_0_header';
                node.margin.top -= parentBounds.y - swimLaneBounds.y - diagram.nameTable[`${laneHeaderId}`].wrapper.bounds.height;
            }
        }
        const container = diagram.nameTable[parent.id];
        if (!container.children) {
            container.children = [];
        }
        if (container.children.indexOf(node.id) === -1) {
            removeChildrenInLane(diagram, node);
            if (diagram.getObject(node.id)) {
                diagram.removeElements(node);
            }
            const undoObj = cloneObject(node);
            diagram.addChild(container, child);
            node = diagram.getObject(node.id);
            if (container.isLane && container.parentId) {
                swimlane = diagram.nameTable[container.parentId];
                const lanes = swimlane.shape.lanes;
                const canvasId = (container.id.slice(swimlane.id.length));
                const currentParentId = canvasId.substring(0, canvasId.length - 1);
                for (let i = 0; i < lanes.length; i++) {
                    if (container.isLane && currentParentId === lanes[parseInt(i.toString(), 10)].id) {
                        // eslint-disable-next-line
                        if (node.parentObj instanceof Diagram || (node.parentObj instanceof Lane)) {
                            // eslint-disable-next-line
                            node.parentObj = lanes[parseInt(i.toString(), 10)];
                            node.propName = 'children';
                        }
                        // 878719: Resolve ESLint errors
                        // eslint-disable-next-line no-prototype-builtins
                        if (!diagram.nameTable.hasOwnProperty(node.id)) {
                            lanes[parseInt(i.toString(), 10)].children.push(node);
                        }
                    }
                }
            }
            diagram.updateDiagramObject(node);
            moveSwinLaneChild(node, diagram);
            if (!container.parentId) {
                diagram.updateDiagramObject(container);
            }
            else if (!isUndo) {
                if (!diagram.multiselect) {
                    updateLaneBoundsAfterAddChild(container, swimlane, node, diagram);
                }
                else {
                    considerSwimLanePadding(diagram, node, 20);
                    diagram.updateDiagramElementQuad();
                }
            }
            if (!(diagram.diagramActions & DiagramAction.UndoRedo)) {
                const entry = {
                    type: 'ChildCollectionChanged', category: 'Internal',
                    undoObject: undoObj, redoObject: cloneObject(node), historyAction: historyAction ? 'AddNodeToLane' : undefined
                };
                diagram.addHistoryEntry(entry);
            }
        }
        else if (!isUndo && diagram.multiselect) {
            considerSwimLanePadding(diagram, node, 20);
            diagram.updateDiagramElementQuad();
        }
        diagram.protectPropertyChange(false);
    }
}
function moveSwinLaneChild(node, diagram) {
    const sourceNode = getDiagramElement(node.id + '_groupElement', diagram.element.id);
    const targetId = (node.parentId) ? node.parentId + '_groupElement' : diagram.element.id + '_diagramLayer';
    const targetNode = getDiagramElement(targetId, diagram.element.id);
    if (sourceNode && targetNode) {
        targetNode.appendChild(sourceNode);
    }
}
export function updateLaneBoundsWithSelector(container, selector, diagram, isBoundsUpdate) {
    const swimLane = diagram.nameTable[container.parentId];
    const isSelector = true;
    updateLaneBoundsAfterAddChild(container, swimLane, selector, diagram, isBoundsUpdate, isSelector);
}
export function updateLaneBoundsAfterAddChild(container, swimLane, node, diagram, isBoundsUpdate, isSelector) {
    const undoObject = cloneObject(container);
    let isUpdateRow;
    let isUpdateColumn;
    let isGroupAction = false;
    const padding = swimLane.shape.padding;
    const containerBounds = container.wrapper.bounds;
    const containerOuterBounds = container.wrapper.outerBounds;
    const nodeBounds = node.wrapper.bounds;
    if (swimLane && swimLane.shape.type === 'SwimLane' &&
        (containerBounds.right < nodeBounds.right + padding ||
            containerBounds.bottom < nodeBounds.bottom + padding || (isSelector && containerBounds.top > nodeBounds.top + padding))) {
        const grid = swimLane.wrapper.children[0];
        const x = grid.bounds.x;
        const y = grid.bounds.y;
        let size;
        let headerSize;
        // EJ2-913788 Drag and drop a node with more size than lane height and width
        const isHeader = container.wrapper.children.find((child) => child.id.includes('header'));
        const isHorizontal = (swimLane.shape.orientation === 'Horizontal');
        if (containerBounds.right < nodeBounds.right + padding &&
            containerOuterBounds.x <= containerBounds.x) {
            if (isHorizontal && isHeader) {
                headerSize = isHeader ? isHeader.width : 0;
                size = (nodeBounds.right - containerBounds.right) + (containerBounds.left - nodeBounds.left) +
                    headerSize + padding * 2;
            }
            else {
                size = (nodeBounds.right - containerBounds.right) + (containerBounds.left - nodeBounds.left) + padding * 2;
            }
            // size = nodeBounds.right - containerBounds.right;
            isUpdateColumn = true;
            grid.updateColumnWidth(container.columnIndex, containerBounds.width + size, true, padding);
        }
        if (containerBounds.bottom < nodeBounds.bottom + padding &&
            containerOuterBounds.y <= containerBounds.y) {
            if (!isHorizontal && isHeader) {
                headerSize = isHeader ? isHeader.height : 0;
                size = (nodeBounds.bottom - containerBounds.bottom) + (containerBounds.top - nodeBounds.top) +
                    headerSize + padding * 2;
            }
            else {
                size = (nodeBounds.bottom - containerBounds.bottom) + (containerBounds.top - nodeBounds.top) + padding * 2;
            }
            // size = nodeBounds.bottom - containerBounds.bottom;
            isUpdateRow = true;
            grid.updateRowHeight(container.rowIndex, containerBounds.height + size, true, padding);
        }
        if (isSelector && containerBounds.top > nodeBounds.top - padding &&
            containerOuterBounds.y <= containerBounds.y) {
            if (!isHorizontal && isHeader) {
                headerSize = isHeader ? isHeader.height : 0;
                size = (containerBounds.top - nodeBounds.top) + (nodeBounds.bottom - containerBounds.bottom) +
                    headerSize + padding * 2;
            }
            else {
                size = (containerBounds.top - nodeBounds.top) + (nodeBounds.bottom - containerBounds.bottom) + padding * 2;
            }
            // Adjust the size based on the top boundary difference
            if (size > 0) {
                isUpdateRow = true;
                grid.updateRowHeight(container.rowIndex, containerBounds.height + size, true, padding);
            }
        }
        diagram.isRowHeightUpdate = isUpdateRow;
        if (!(diagram.diagramActions & DiagramAction.UndoRedo)) {
            if (isBoundsUpdate) {
                diagram.startGroupAction();
                isGroupAction = true;
            }
            if (isUpdateRow) {
                const entry = {
                    category: 'Internal',
                    type: 'RowHeightChanged',
                    undoObject: undoObject, redoObject: cloneObject(container)
                };
                diagram.addHistoryEntry(entry);
            }
            if (isUpdateColumn) {
                const entry = {
                    category: 'Internal',
                    type: 'ColumnWidthChanged',
                    undoObject: undoObject, redoObject: cloneObject(container)
                };
                diagram.addHistoryEntry(entry);
            }
        }
        swimLane.width = swimLane.wrapper.width = grid.width;
        swimLane.height = swimLane.wrapper.height = grid.height;
        swimLaneMeasureAndArrange(swimLane);
        if (swimLane.shape.orientation === 'Horizontal') {
            updatePhaseMaxWidth(swimLane, diagram, container.wrapper, container.columnIndex);
        }
        updateHeaderMaxWidth(diagram, swimLane);
        diagram.drag(swimLane, x - grid.bounds.x, y - grid.bounds.y);
        checkPhaseOffset(swimLane, diagram);
        checkLaneSize(swimLane);
    }
    if (!isSelector) {
        considerSwimLanePadding(diagram, node, padding);
    }
    diagram.updateDiagramElementQuad();
    return isGroupAction;
}
//#endregion
//# reginon stack panel interaction
/**
 * renderStackHighlighter method\
 *
 * @returns {  void  }    renderStackHighlighter method .\
 * @param {DiagramElement} element - provide the element value.
 * @param {boolean} isVertical - provide the isVertical value.
 * @param {PointModel} position - provide the position value.
 * @param {Diagram} diagram - provide the diagram value.
 * @param {boolean} isUml - provide the isUml value.
 * @param {boolean} isSwimlane - provide the isSwimlane value.
 * @private
 */
export function renderStackHighlighter(element, isVertical, position, diagram, isUml, isSwimlane) {
    const adornerSvg = getAdornerLayerSvg(diagram.element.id);
    diagram.diagramRenderer.renderStackHighlighter(element, adornerSvg, diagram.scroller.transform, isVertical, position, isUml, isSwimlane);
}
/**
 * moveChildInStack method\
 *
 * @returns {  void }    moveChildInStack method .\
 * @param {Node} sourceNode - provide the sourceNode value.
 * @param {Node} target - provide the target value.
 * @param {Diagram} diagram - provide the diagram value.
 * @param {Actions} action - provide the action value.
 * @private
 */
export function moveChildInStack(sourceNode, target, diagram, action) {
    const obj = sourceNode;
    const parent = diagram.nameTable[obj.parentId];
    const sourceParent = diagram.nameTable[obj.parentId];
    if (target && sourceParent && sourceParent.container && sourceParent.container.type === 'Stack' &&
        target.container && target.container.type === 'Stack' && (sourceParent.id !== target.parentId)) {
        const value = sourceParent.wrapper.children.indexOf(obj.wrapper);
        if (value > -1) {
            diagram.nameTable[obj.id].parentId = target.id;
            sourceParent.wrapper.children.splice(value, 1);
        }
    }
    if (target && target.parentId && obj.parentId && action === 'Drag' &&
        sourceParent.container && sourceParent.container.type === 'Stack') {
        const targetIndex = parent.wrapper.children.indexOf(target.wrapper);
        const sourceIndex = parent.wrapper.children.indexOf(obj.wrapper);
        const undoElement = {
            targetIndex: targetIndex, target: target,
            sourceIndex: sourceIndex, source: sourceNode
        };
        parent.wrapper.children.splice(sourceIndex, 1);
        parent.wrapper.children.splice(targetIndex, 0, obj.wrapper);
        const redoElement = {
            targetIndex: sourceIndex, target: target,
            sourceIndex: targetIndex, source: sourceNode
        };
        const entry = {
            type: 'StackChildPositionChanged', redoObject: redoElement,
            undoObject: undoElement, category: 'Internal'
        };
        diagram.commandHandler.addHistoryEntry(entry);
    }
}
//#end region
//# region Swimlane rendering
//#end region
